import { Component, inject, InjectFlags, Input, OnInit, Renderer2 } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  Distance,
  HoursPerWeekPreference,
  Option,
  SalaryPeriod,
  UserPreferredEducationLevel,
  VacancyPreferencesModel,
} from '@digilize/shared/definitions/src';
import { MeApiService } from '@digilize/shared/data/services/src';
import { TranslateService } from '@ngx-translate/core';

import { CvService } from '../../../services/cv.service';
import { WizardService } from '../../../services/wizard.service';

@Component({
  selector: 'bwc-education-level-selection',
  templateUrl: './education-level-selection.component.html',
  styleUrls: ['./education-level-selection.component.scss'],
})
export class EducationLevelSelectionComponent implements OnInit {
  @Input() prevBut: boolean;
  @Input() closeBut: boolean;

  educationLevelControl = new FormControl('');
  educationLevelOptions: Option[] = [];
  dataLoaded = false;

  wizardService: WizardService | null = inject(WizardService, InjectFlags.Optional);
  private readonly cvService = inject(CvService);
  private readonly meApiService = inject(MeApiService);
  private readonly translateService = inject(TranslateService);
  private readonly renderer = inject(Renderer2);

  private selectedEducationLevel: UserPreferredEducationLevel | null = null;
  private existingPreferences: VacancyPreferencesModel | null = null;

  private readonly educationLevelMap: { value: UserPreferredEducationLevel; labelKey: string }[] = [
    { value: UserPreferredEducationLevel.Lbo, labelKey: 'work_preferences.education_level_lbo' },
    { value: UserPreferredEducationLevel.Vmbo, labelKey: 'work_preferences.education_level_vmbo' },
    { value: UserPreferredEducationLevel.Havo, labelKey: 'work_preferences.education_level_havo' },
    { value: UserPreferredEducationLevel.Vwo, labelKey: 'work_preferences.education_level_vwo' },
    { value: UserPreferredEducationLevel.Mbo, labelKey: 'work_preferences.education_level_mbo' },
    { value: UserPreferredEducationLevel.Hbo, labelKey: 'work_preferences.education_level_hbo' },
    { value: UserPreferredEducationLevel.Wo, labelKey: 'work_preferences.education_level_wo' },
    { value: UserPreferredEducationLevel.Phd, labelKey: 'work_preferences.education_level_phd' },
  ];

  ngOnInit(): void {
    this.renderer.removeClass(document.body, 'profile-check');
    this.loadData();
  }

  onEducationLevelSelected(option: Option): void {
    this.selectedEducationLevel = Number(option.val) as UserPreferredEducationLevel;
  }

  onSubmit(): void {
    if (this.selectedEducationLevel == null) {
      this.wizardService?.goToNext();
      return;
    }

    const prefs: VacancyPreferencesModel = {
      distance: this.existingPreferences?.distance ?? Distance.Unknown,
      city: this.existingPreferences?.city ?? null,
      salary: this.existingPreferences?.salary ?? null,
      salary_period: this.existingPreferences?.salary_period ?? SalaryPeriod.Monthly,
      hours_range: this.existingPreferences?.hours_range ?? HoursPerWeekPreference.HFlexible,
      education_level: this.selectedEducationLevel,
      dream_professions: this.existingPreferences?.dream_professions ?? null,
    };

    this.meApiService.updatePreferences(prefs).subscribe({
      next: () => this.wizardService?.goToNext(),
      error: () => this.wizardService?.goToNext(),
    });
  }

  goPrev(): void {
    this.wizardService?.goToPrevious();
  }

  private loadData(): void {
    const translationKeys = this.educationLevelMap.map((e) => e.labelKey);

    this.translateService.get(translationKeys).subscribe((translations) => {
      this.educationLevelOptions = this.educationLevelMap.map((e) => ({
        id: String(e.value),
        text: translations[e.labelKey],
        val: String(e.value),
      }));
    });

    this.cvService.getCV().subscribe({
      next: (cv) => {
        this.existingPreferences = cv.vacancy_preferences ?? null;

        if (cv.vacancy_preferences?.education_level != null) {
          this.selectedEducationLevel = cv.vacancy_preferences.education_level;

          const entry = this.educationLevelMap.find((e) => e.value === cv.vacancy_preferences.education_level);
          if (entry) {
            this.translateService.get(entry.labelKey).subscribe((label) => {
              this.educationLevelControl.setValue(label);
            });
          }
        }

        this.dataLoaded = true;
      },
      error: () => {
        this.dataLoaded = true;
      },
    });
  }
}
